import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Row } from "react-bootstrap";
import Typography from "@material-ui/core/Typography";
import "react-dropdown-now/style.css";
import { useTranslation } from "react-i18next";
import Loader from "../../../components/Loader";
import { ML_MODEL_URL } from "../../../constants/api";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import UploadImage from "../../../assets/cloud-upload.svg";
import { MediumUploadButtonStyle } from "../../../components/ButtonStyle";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import { listDiseaseDetectionHistory } from "../../../actions/MlAction";
import { useNavigate } from "react-router-dom";
import { Container, Grid, Card, CardContent, Divider } from "@mui/material";
import { styled } from "@mui/material/styles";

const GradientPaper = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(170deg, rgba(191, 226, 186, 0.95), rgba(92,167,201,0.95))',
  borderRadius: '16px',
  padding: theme.spacing(3),
  color: '#fff',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 40px 0 rgba(31, 38, 135, 0.4)',
  }
}));

const DiseaseCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(191, 226, 186, 0.3)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 4px 20px rgba(92,167,201,0.2)',
    borderColor: 'rgba(92,167,201,0.5)',
  }
}));

const DiseaseCardContent = styled(CardContent)({
  flexGrow: 1,
  padding: '16px',
});

const ResultsContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(170deg, rgba(191, 226, 186, 0.1), rgba(92,167,201, 0.1))',
  borderRadius: '20px',
  padding: theme.spacing(3),
  marginTop: theme.spacing(4),
}));

function DiseasePredict({setImage}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const xsmall = useMediaQuery(theme.breakpoints.down("xs"));
  const medium = useMediaQuery(theme.breakpoints.up("md"));
  const { t, i18n } = useTranslation();
  const [uploadimage, setUploadimage] = useState("");
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    
    const file = e.dataTransfer.files[0];
    if (file && (file.type === "image/jpeg" || file.type === "image/jpg")) {
      setUploadimage(file);
      setPreview(URL.createObjectURL(file));
      setImage(URL.createObjectURL(file));
    }
  };
  
  const setImg = (evt) => {
    const file = evt.target.files[0];
    setUploadimage(file);
    setPreview(URL.createObjectURL(file));
    setImage(URL.createObjectURL(file));
  };

  const SavedResult = (
    disease_class_name,
    crop_name,
    disease_name,
    accuracy,
    file_name,
    status
  ) => {
    const uploadData = new FormData();
    uploadData.append("disease_class_name", disease_class_name);
    uploadData.append("ln", i18n.language);
    try {
      setLoading(true);
      fetch(`${ML_MODEL_URL}disease-predict-data`, {
        method: "POST",
        body: uploadData,
      })
        .then((response) => response.json())
        .then((data) => {
          setLoading(false);
          data.data["crop"] = crop_name;
          data.data["disease"] = disease_name;
          data.data["percentage"] = accuracy;
          data.data["file_name"] = file_name;
          data.data["status"] = status;
          navigate('/disease-prediction-result', {state: { result: data.data, percentage: data.percentage}});
        });
    } catch (error) {
      console.log(error);
    }
  };

  const SaveData = (user_id) => {
    setLoading(true);
    const uploadData = new FormData();
    uploadData.append("file", uploadimage, uploadimage.name);
    uploadData.append("ln", i18n.language);
    uploadData.append("user_id", user_id);
    try {
      fetch(`${ML_MODEL_URL}disease-predict`, {
        method: "POST",
        body: uploadData,
      })
        .then((response) => response.json())
        .then((data) => {
          setLoading(false);
          navigate('/disease-prediction-result', {state: { result: data.data, percentage: data.percentage }});
        });
    } catch (error) {
      console.log(error);
    }
  };

  const dispatch = useDispatch();
  const {user} = useSelector((state) => state.auth);

  const DiseaseDetectionHistory = useSelector(
    (state) => state.DiseaseDetectionHistory
  );
  const {
    disease_detection_history,
    disease_detection_history_loading,
  } = DiseaseDetectionHistory;

  useEffect(() => {
    if (user) {
      dispatch(listDiseaseDetectionHistory(user.id, i18n.language));
    }
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {loading ? (
        <Loader loading={loading}/>
      ) : (
        <>
          <Box sx={{ 
            textAlign: 'center',
            mb: 6,
            background: 'linear-gradient(170deg, rgba(191, 226, 186, 0.2), rgba(92,167,201, 0.2))',
            py: 4,
            borderRadius: '20px'
          }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.75rem", md: "2.5rem" },
                color: "black",
                mb: 2
              }}
            >
              {t("diseaseDetection")}
            </Typography>
            
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: "1rem", md: "1.1rem" },
                color: "text.secondary",
                maxWidth: "600px",
                mx: "auto"
              }}
            >
              Upload a plant image to detect diseases and get detailed information about treatment
            </Typography>
          </Box>

          <GradientPaper elevation={3}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  mb: 1
                }}
              >
                {t("uploadImage")}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  opacity: 0.9,
                  fontSize: { xs: "0.875rem", md: "1rem" }
                }}
              >
                {t("jpgAllowed")}
              </Typography>
            </Box>

            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                m: 2,
                minHeight: 300,
                backgroundColor: '#fff',
                border: preview ? '2px solid rgba(191, 226, 186, 1)' : '2px dashed rgba(92,167,201,0.5)',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(92,167,201,1)',
                  boxShadow: '0 4px 20px rgba(92,167,201,0.1)'
                }
              }}
            >
              <img
                width={preview ? "auto" : 81}
                height={preview ? 300 : 81}
                src={preview ? preview : UploadImage}
                alt="Upload preview"
                style={{
                  maxWidth: "100%",
                  objectFit: preview ? "contain" : "initial",
                  borderRadius: preview ? "8px" : 0,
                  transition: "all 0.3s ease"
                }}
              />
              {!preview && (
                <Typography
                  sx={{
                    mt: 2,
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "rgba(92,167,201,1)"
                  }}
                >
                  {t("dragDropBrowse")}
                </Typography>
              )}
              <input
                type="file"
                onChange={(evt) => setImg(evt)}
                accept="image/jpeg,image/jpg"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                  cursor: "pointer"
                }}
              />
            </Paper>
          </GradientPaper>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 4, mb: 6 }}
          >
            <Button
              style={{
                ...MediumUploadButtonStyle,
                opacity: !uploadimage ? 0.7 : 1,
                transition: "all 0.3s ease"
              }}
              disabled={!uploadimage || loading}
              onClick={() => SaveData(user.id)}
            >
              {t("Submit")}
              <i className="fas fa-angle-right" style={{ marginLeft: 8 }} />
            </Button>
          </Stack>

          {!disease_detection_history_loading && disease_detection_history?.length > 0 && (
            <ResultsContainer>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mb: 4,
                  color: "black",
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                  textAlign: "center"
                }}
              >
                {t("PreviousResult")}
              </Typography>
              
              <Grid container spacing={3}>
                {disease_detection_history.map((data, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <DiseaseCard
                      onClick={() =>
                        SavedResult(
                          data.prediction,
                          data.crop_name,
                          data.disease_name,
                          data.accuracy,
                          data.file_name,
                          data.status
                        )
                      }
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          paddingTop: '75%', // 4:3 Aspect Ratio
                          overflow: 'hidden',
                        }}
                      >
                        <img
                          src={data.disease_image}
                          alt={data.disease_name}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      </Box>
                      <DiseaseCardContent>
                        <Typography 
                          variant="h6" 
                          gutterBottom
                          sx={{ 
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            color: 'rgba(0, 0, 0, 0.87)'
                          }}
                        >
                          {data.disease_name}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.secondary',
                            mb: 1
                          }}
                        >
                          <strong>{t("cropname")}:</strong> {data.crop_name}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: data.accuracy > 70 ? 'success.main' : 'warning.main',
                            fontWeight: 500
                          }}
                        >
                          <strong>{t("accuracy")}:</strong> {data.accuracy}%
                        </Typography>
                        {data.status === "True" && data.predicted_accurately === "No" && (
                          <Typography 
                            color="error" 
                            variant="body2" 
                            sx={{ 
                              mt: 1,
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            <i className="fas fa-exclamation-circle" />
                            False Prediction
                          </Typography>
                        )}
                      </DiseaseCardContent>
                    </DiseaseCard>
                  </Grid>
                ))}
              </Grid>
            </ResultsContainer>
          )}
        </>
      )}
    </Container>
  );
}

export default DiseasePredict;
